import { useCallback, useEffect, useState } from 'react';
import { identityApi, IdentityError, getIdentitySession, setIdentitySession } from '../../lib/identityApi';

// Portal 管理（Integral 内集中管理）。
// 首版严格模式：只有被显式授予 portal.manage 的账号才能进入本页；
// 未获授权时后端返回 portal_manage_required，这里只做提示，不做前端兜底放行。
//
// 本页使用统一身份服务的会话（x-integral-session），与 core-api 自身的 Bearer
// 会话彼此独立，因此自带登录入口。

interface PortalFeature { key: string; label: string; legacy: string | null; }
interface PortalCustomer {
  userId: number;
  username: string;
  isActive: boolean;
  customerCode: string | null;
  role: string;
  features: Record<string, boolean>;
  adminCustomerScope: string[];
  mustSetPassword: boolean;
}
interface AuditRow { id: number; action: string; target_user_id: number | null; detail: string | null; created_at: string; }

const ERROR_TEXT: Record<string, string> = {
  portal_manage_required: '未获授权：本页面需要管理员显式授予 portal.manage 权限',
  session_required: '会话已失效，请重新登录',
  session_invalid: '会话无效，请重新登录',
  session_expired: '会话已过期，请重新登录',
  account_disabled: '账号已被禁用',
};

function errText(e: unknown): string {
  if (e instanceof IdentityError) return ERROR_TEXT[e.message] || e.message;
  return '操作失败';
}

export function PortalAdminPage() {
  const [session, setSession] = useState<string>(getIdentitySession());
  const [creds, setCreds] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loggingIn, setLoggingIn] = useState(false);
  const [customers, setCustomers] = useState<PortalCustomer[]>([]);
  const [features, setFeatures] = useState<PortalFeature[]>([]);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ username: '', customer_code: '', customer_name: '' });
  const [setLink, setSetLink] = useState<string | null>(null);
  // 被拒绝进入的提示：会话无效或缺少 portal.manage 时显示在登录表单上。
  const [denied, setDenied] = useState<string | null>(null);

  const doLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoggingIn(true); setLoginError(null);
    try {
      const r = await identityApi.login(creds.username, creds.password);
      setIdentitySession(r.sessionToken);
      setSession(r.sessionToken);
      setCreds({ username: '', password: '' });
    } catch (err) {
      setLoginError(err instanceof IdentityError ? '用户名或密码错误，或账号尚未完成密码设置' : '登录失败');
    } finally { setLoggingIn(false); }
  };

  const load = useCallback(async () => {
    try {
      const [cs, fs, au] = await Promise.all([
        identityApi.customers<PortalCustomer>(),
        identityApi.features<PortalFeature>(),
        identityApi.audit<AuditRow>(),
      ]);
      setCustomers(cs); setFeatures(fs); setAudit(au); setError(null); setDenied(null);
    } catch (e) {
      const msg = errText(e);
      setError(msg);
      // 只有「会话确实失效」(401) 才清除会话。
      // 403 portal_manage_required 表示**会话有效但无管理权限** —— 此时清除会话会把
      // 用户踢出登录状态，既不必要也会让人误以为登录失败；正确做法是保留会话并提示。
      if (e instanceof IdentityError && e.status === 401) {
        setIdentitySession(''); setSession('');
      } else if (e instanceof IdentityError && e.message === 'portal_manage_required') {
        setDenied(msg);
      }
    }
  }, []);

  useEffect(() => { if (session) void load(); }, [session, load]);

  const run = useCallback(async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); await load(); setError(null); }
    catch (e) { setError(errText(e)); }
    finally { setBusy(false); }
  }, [load]);

  const createCustomer = () => run(async () => {
    const res = await identityApi.createCustomer({
      username: form.username, customer_code: form.customer_code, customer_name: form.customer_name,
    });
    setSetLink(`${location.origin}/set-password?token=${res.setPasswordToken}`);
    setForm({ username: '', customer_code: '', customer_name: '' });
  });

  const toggleFeature = (u: PortalCustomer, key: string) => run(() =>
    identityApi.setFeatures(u.userId, { ...u.features, [key]: !u.features[key] }));

  const toggleActive = (u: PortalCustomer) => run(() =>
    u.isActive ? identityApi.disable(u.userId) : identityApi.enable(u.userId));

  const resetPassword = (u: PortalCustomer) => run(async () => {
    const res = await identityApi.resetPassword(u.userId);
    setSetLink(`${location.origin}/set-password?token=${res.token}`);
  });

  // 切换账号：显式退出身份会话。会话有效但无管理权限时不会自动清除，
  // 因此必须提供一个明确的换人入口。
  const switchAccount = async () => {
    try { await identityApi.logout(); } catch { /* 即便登出失败也清本地会话 */ }
    setIdentitySession(''); setSession('');
    setCustomers([]); setFeatures([]); setAudit([]);
    setError(null); setDenied(null);
  };

  if (!session) {
    // 样式：完全沿用 LoginPage 既有的 login-* 体系（这些类名确实存在于样式表）。
    // 早期版本曾用 page / card / page-head / alert-error，这些类名在项目样式表中
    // 并不存在，导致输入框退化为浏览器默认外观（现场验收发现）。
    return (
      <div className="login-wrap">
        <div className="login-pane">
          <form className="login-card" onSubmit={doLogin} data-testid="identity-login-form">
            <div className="login-brand">
              <div className="brand-mark logo"><img src="/logo.png" alt="Pinnacle Laser" /></div>
              <div className="brand-text">
                <b>Portal 管理</b>
                <span>Pinnacle Laser · 统一身份</span>
              </div>
            </div>

            <div className="login-form">
              <label className="field">
                <label>用户名</label>
                <input className="txt" data-testid="login-username" type="text" autoComplete="username"
                  value={creds.username} onChange={e => setCreds({ ...creds, username: e.target.value })} autoFocus />
              </label>
              <label className="field">
                <label>密码</label>
                <input className="txt" data-testid="login-password" type="password" autoComplete="current-password"
                  value={creds.password} onChange={e => setCreds({ ...creds, password: e.target.value })} />
              </label>
              {denied ? <div className="notice bad" role="alert">{denied}</div> : null}
              {loginError ? <div className="notice bad" role="alert">{loginError}</div> : null}
              <button className="btn primary" type="submit" data-testid="login-submit"
                disabled={loggingIn || !creds.username || !creds.password}
                style={{ justifyContent: 'center', padding: '10px 12px' }}>
                {loggingIn ? '正在验证…' : '登录'}
              </button>
            </div>

            <div className="login-foot">
              需由管理员显式授予 portal.manage 权限。此处使用统一身份会话，与 Integral 主登录相互独立。
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* 页面标题由 AppLayout 的 topbar 提供；这里只放页面级操作与说明。
          切换账号是必需的：会话有效但无 portal.manage 时后端不会清会话，
          没有这个入口用户会卡在「看似已登录但所有请求被拒」的状态。 */}
      <div className="toolbar" style={{ marginBottom: 'var(--gap)' }}>
        <span className="muted">客户账号、功能权限与管理员可访问客户范围，统一在 Integral 内管理。</span>
        <div className="spacer" />
        <button className="btn ghost" type="button" data-testid="switch-account" onClick={switchAccount}>
          切换账号
        </button>
      </div>

      {error && <div className="notice bad" role="alert" style={{ marginBottom: 12 }}>{error}</div>}

      {setLink && (
        <div className="notice info" style={{ marginBottom: 12 }}>
          <p>请把下面的一次性链接发给客户（仅显示一次，使用后即失效）：</p>
          <code data-testid="set-password-link">{setLink}</code>
          <button className="btn ghost" type="button" onClick={() => setSetLink(null)}>关闭</button>
        </div>
      )}

      <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
        <div className="panel-head"><div><h2>新建客户账号</h2>
          <div className="desc">创建后不设置密码，通过一次性链接让客户自行设置</div></div></div>
        <div className="panel-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
            <label className="field">
              <label>用户名</label>
              <input className="txt" data-testid="new-username" value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })} />
            </label>
            <label className="field">
              <label>客户编码</label>
              <input className="txt" data-testid="new-customer-code" value={form.customer_code}
                onChange={e => setForm({ ...form, customer_code: e.target.value })} />
            </label>
            <label className="field">
              <label>客户名称</label>
              <input className="txt" data-testid="new-customer-name" value={form.customer_name}
                onChange={e => setForm({ ...form, customer_name: e.target.value })} />
            </label>
          </div>
          <button className="btn primary" type="button" data-testid="create-customer"
            disabled={busy || !form.username || !form.customer_code} onClick={createCustomer}
            style={{ marginTop: 12 }}>
            创建账号（不设密码）
          </button>
        </div>
      </section>

      <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
        <div className="panel-head"><div><h2>客户账号与功能权限</h2></div></div>
        <div className="panel-body">
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>用户名</th><th>客户</th><th>状态</th>
                  {features.map(f => <th key={f.key} title={f.legacy ? `原字段：${f.legacy}` : undefined}>{f.label}</th>)}
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {customers.map(u => (
                  <tr key={u.userId}>
                    <td>{u.username}{u.mustSetPassword && <small className="muted"> · 待设置密码</small>}</td>
                    <td>{u.customerCode || '—'}</td>
                    <td>{u.isActive ? '启用' : '已禁用'}</td>
                    {features.map(f => (
                      <td key={f.key}>
                        <input
                          type="checkbox"
                          aria-label={`${u.username} ${f.label}`}
                          checked={!!u.features[f.key]}
                          disabled={busy}
                          onChange={() => toggleFeature(u, f.key)}
                        />
                      </td>
                    ))}
                    <td>
                      <div className="row-actions">
                        <button className="btn link" type="button" disabled={busy} onClick={() => toggleActive(u)}>
                          {u.isActive ? '禁用' : '启用'}
                        </button>
                        <button className="btn link" type="button" disabled={busy} onClick={() => resetPassword(u)}>
                          重置密码
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
        <div className="panel-head"><div><h2>操作审计</h2></div></div>
        <div className="panel-body">
          <div className="table-wrap">
            <table className="grid">
              <thead><tr><th>时间</th><th>动作</th><th>目标用户</th><th>详情</th></tr></thead>
              <tbody>
                {audit.map(a => (
                  <tr key={a.id}>
                    <td>{a.created_at}</td><td>{a.action}</td>
                    <td>{a.target_user_id ?? '—'}</td><td>{a.detail ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}